import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
  NO_ERRORS_SCHEMA,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ToastrModule } from 'ngx-toastr';
import { provideNgxWebstorage, withNgxWebstorageConfig, withLocalStorage, withSessionStorage } from 'ngx-webstorage';
import { AppComponent } from './app.component';
import { APP_RESOLVER_PROVIDERS } from './app.resolver';
import { ROUTES } from './app.routes';
import { AppState } from './app.service';
import { BreadcrumbComponent } from './breadcrumbs/breadcrumbs.component';
import { BreadcrumbsService } from './breadcrumbs/breadcrumbs.service';
import { CartService } from './cart/cart.service';
import { CartSharedModule } from './cartShared/cartShared.module';
import { CookiePopupComponent } from './cookie-popup/cookie.popup.component';
import { CookieService } from './cookie-popup/cookie.service';
import { ErrorShared } from './error/ErrorShared';
import { AuthGuard } from './guard.service';
import { HomeComponent } from './home/home.component';
import { IntegrationComponent } from './integration/integration.component';
import { IntegrationService, integrationInitializer } from './integration/integration.service';
import { LocalizationInterceptor } from './interceptors/localization.interceptor';
import { SessionInterceptor } from './interceptors/session.interceptor';
import { LoginSharedService } from './login/login-shared.service';
import { LoginService } from './login/login.service';
import { LogoutService } from './logout/logout.service';
import { OptionDetailModule } from './optionDetail/optionDetail.module';
import { SharedPartDetail } from './optionDetail/partDetail/sharedPartDetails';
import { AppSharedModule } from './shared.module';
import { FooterComponent } from './shared/footer/footer.component';
import { GenericNotificationComponent } from './shared/generic-notification/generic-notification.component';
import { HeaderComponent } from './shared/header/header.component';
import { HeaderService } from './shared/header/header.service';
import { NotificationService } from './shared/header/notification.service';
import { TicketCompleteComponent } from './ticket-complete/ticket-complete.component';
import { TicketCompleteService } from './ticket-complete/ticket-complete.service';
import { TreeManipulator } from './tree-node/treenode.service';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule, NgxsModuleOptions } from '@ngxs/store';
import { environment } from '../environments/environment';
import { AngularMaterialModule } from './angular-material.module';
import { LoginAuthGuard } from './auth.guard';
import { RouterState } from './breadcrumbs/state/router.state';
import { BlobStorageService } from './cms-editor/services/api-services/cms-blob.service';
import { SharingServices } from './cms-editor/services/sharing-service';
import { DealerNewslettersComponent } from './dealer-newsletters/dealer-newsletters.component';
import { ErrorModule } from './error/error.module';
import { GsnAuthGuard } from './gsn-guard.service';
import { ResourceCacheService } from './interceptors/resource-cache.service';
import { MSTSharedService } from './mechanical-service-tools/mst-shared.service';
import { OptionDetailComponentState } from './optionDetail/state/option-detail.state';
import { SharedInfoService } from './partsCatalog/sharedInfo.service';
import { EngineBOMState } from './partsCatalog/state/engine-bom.state';
import { PowerGenSharedInfoService } from './power-gen/powerGenSharedInfo.service';
import { PreLoginAuthGuard } from './prelogin.auth.guard';
import { SAMLComponent } from './saml/saml.component';
import { AccessCheckComponent } from './shared/check-access/check-access.component';
import { ApplicationStatePluginModule } from './state/app-plugins.module';
import { ApplicationState } from './state/app.state';

import { DataPlateService } from './dataplate/dataplate.service';
import { LocalizationService, localizationInitializer } from './localization.service';
import { FEATURE_FUNCTIONS } from './shared/feature-checker.service';
import * as FeatureFunctions from './shared/feature-functions';

import { ExportCsvService } from './export-to-csv/export.service';
import { ToasterService } from './toaster/toaster.service';

import { STORAGE_PREFIX, STORAGE_SEPARATOR } from './app.constants';
import { runtimeConfigInitializer } from './runtime-config.service';

import { providePrimeNG } from 'primeng/config';
import { Nova } from './primeng/themes/nova';

export const NGXS_MODULE_OPTS: NgxsModuleOptions = {
    developmentMode: !environment.production,
    compatibility: { strictContentSecurityPolicy: true },
    selectorOptions: {
        suppressErrors: false,
        injectContainerState: false
    }
};

const APP_PROVIDERS = [
    ...APP_RESOLVER_PROVIDERS,
    AppState
];
@NgModule({
    declarations: [
        AppComponent,
        CookiePopupComponent,
        HeaderComponent,
        HomeComponent,
        FooterComponent,
        BreadcrumbComponent,
        GenericNotificationComponent,
        IntegrationComponent,
        TicketCompleteComponent,
        DealerNewslettersComponent,
        AccessCheckComponent,
        SAMLComponent,
    ],
    imports: [
        AppSharedModule,
        MatProgressBarModule,
        AngularMaterialModule,
        MatProgressSpinnerModule,
        BrowserModule,
        CartSharedModule.forRoot(),
        ErrorModule,
        PopoverModule,
        FormsModule,
        BrowserAnimationsModule,
        OptionDetailModule,
        RouterModule,
        ReactiveFormsModule,
        HttpClientModule,
        ToastrModule.forRoot({
            preventDuplicates: true,
            tapToDismiss: true,
            timeOut: 5000,
            positionClass: 'toast-top-full-width',
        }),
        ROUTES,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        NgxsModule.forRoot([
            ApplicationState, EngineBOMState, OptionDetailComponentState,
            RouterState
        ], NGXS_MODULE_OPTS),
        NgxsReduxDevtoolsPluginModule.forRoot({
            name: 'IAC-Store',
            disabled: environment.production
        }),
        ApplicationStatePluginModule.forRoot(),
    ],
    // expose our Services and Providers into Angular's dependency injection
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideNgxWebstorage(
            withNgxWebstorageConfig({ prefix: STORAGE_PREFIX, separator: STORAGE_SEPARATOR }),
            withLocalStorage(),
            withSessionStorage()
        ),
        SharedPartDetail,
        APP_PROVIDERS,
        LoginService,
        ErrorShared,
        CookieService,
        IntegrationService,
        LogoutService,
        BreadcrumbsService,
        NotificationService,
        CartService,
        HeaderService,
        TicketCompleteService,
        BlobStorageService,
        SharingServices,
        DataPlateService,
        AuthGuard,
        GsnAuthGuard,
        ResourceCacheService,
        PreLoginAuthGuard,
        LoginAuthGuard,
        TreeManipulator,
        SharedInfoService,
        PowerGenSharedInfoService,
        MSTSharedService,
        LocalizationService,
        ToasterService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SessionInterceptor,
            multi: true
        }, {
            provide: FEATURE_FUNCTIONS,
            useClass: FeatureFunctions.CheckObjectFieldFunction,
            multi: true
        }, {
            provide: FEATURE_FUNCTIONS,
            useClass: FeatureFunctions.TrueFunction,
            multi: true
        }, {
            provide: HTTP_INTERCEPTORS,
            useClass: LocalizationInterceptor,
            multi: true
        },
        provideAppInitializer(runtimeConfigInitializer),
        provideAppInitializer(localizationInitializer),
        provideAppInitializer(integrationInitializer),
        ExportCsvService,
        providePrimeNG({
            theme: {
                preset: Nova,
                options: {
                    darkModeSelector: 'none' // '.my-app-dark' // Overrides and disables automatic dark mode flipping
                }
            }
        }),
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class AppModule {
    constructor(readonly loginShared: LoginSharedService) {
        this.loginShared.registeredFeatures.set('dealer-newsletters', ['DEALER_NEWS', 'IACDataServices']);
        this.loginShared.registeredFeatures.set('add-upfits', ['UPFITS_UPLOAD', 'IACDataServices']);
    }
}

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}
